import Input from "../../components/form/input/InputField";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Modal } from "../../components/ui/modal";
import { CheckCircle, TriangleAlert } from "lucide-react";
import axios from "axios";
import { styles } from "../../styles/pages/Opportunity/OpportunityAddStyles";
import ListboxComponent from "../../components/common/ListboxComponent";
import { DatePicker } from "../../components/common/DatePicker";

// Interfaces
interface Stage {
  id: number;
  stage_name: string;
  is_active?: boolean;
}

interface Source {
  id: number;
  source_name: string;
  is_active?: boolean;
}

// interface User {
//   id: number;
//   name: string;
//   is_active?: boolean;
// }

interface Lead {
  id: number;
  name: string;
}

interface Status {
  id: number;
  status_name: string;
  is_active?: boolean;
}

interface OpportunitieFormData {
  opportunitie_name: string;
  opp_amount: string;
  probability: string;
  stage_id: string;
  source_id: string;
  // user_id: string;
  status_id: string;
  lead_id: string;
  expected_close_date: string;
  description?: string;
}

interface Errors {
  opportunitie_name?: string;
  opp_amount?: string;
  probability?: string;
  stage_id?: string;
  source_id?: string;
  // user_id?: string;
  status_id?: string;
  lead_id?: string;
  expected_close_date?: string;
}

const OpportunityAdd = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<OpportunitieFormData>({
    opportunitie_name: "",
    opp_amount: "",
    probability: "",
    stage_id: "0",
    source_id: "0",
    // user_id: "0",
    status_id: "0",
    lead_id: "0",
    expected_close_date: "",
    description: "",
  });

  const [stages, setStages] = useState<Stage[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  // const [users, setUsers] = useState<User[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [dateOfEvent, setDateOfEvent] = useState<string>("");
  const token = sessionStorage.getItem("access_token");
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchStages = async () => {
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/stage/name/list?is_active=true`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          const data = (await response.json()).data;
          setStages(data);
        }
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };
    fetchStages();
  }, [token]);

  useEffect(() => {
    const fetchSources = async () => {
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/source/name/list?is_active=true`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          const data = (await response.json()).data;
          setSources(data);
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      }
    };
    fetchSources();
  }, [token]);

  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     if (!token) return;
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/api/user/name/list?is_active=true`,
  //         {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }
  //       );
  //       if (response.status === 200) {
  //         const data = (await response.json()).data;
  //         setUsers(data);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching users:", error);
  //     }
  //   };
  //   fetchUsers();
  // }, [token]);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/account/name/list`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          const data = (await response.json()).data;
          setLeads(data);
        }
      } catch (error) {
        console.error("Error fetching leads:", error);
      }
    };
    fetchLeads();
  }, [token]);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (!token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/status/name/list?is_active=true`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.status === 200) {
          const data = (await response.json()).data;
          setStatuses(data);
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, [token]);

  const handleInputChange = (
    field: keyof OpportunitieFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (!value.trim() || value === "0") {
      setErrors((prev) => ({
        ...prev,
        [field]:
          field === "opportunitie_name"
            ? "The opportunity name field is required"
            : field === "opp_amount"
              ? "The amount field is required"
              : field === "probability"
                ? "The probability field is required"
                : field === "stage_id"
                  ? "The stage field is required"
                  : field === "source_id"
                    ? "The source field is required"
                    : field === "status_id"
                      ? "The status field is required"
                      : field === "lead_id"
                        ? "The lead field is required"
                        : undefined, // No error for user_id if not selected
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [field]:
          field === "opp_amount" && Number(value) <= 0
            ? "The amount must be a positive number"
            : field === "opp_amount"
              ? undefined
              : field === "probability" &&
                (isNaN(Number(value)) || Number(value) < 0 || Number(value) > 100)
                ? "Probability must be between 0 and 100"
                : field === "probability"
                  ? undefined
                  : undefined,
      }));
    }
  };

  const validateForm = (): Errors => {
    const newErrors: Errors = {};
    if (!formData.opportunitie_name.trim())
      newErrors.opportunitie_name = "The opportunity name field is required";
    if (!formData.opp_amount)
      newErrors.opp_amount = "The amount field is required";
    else if (isNaN(Number(formData.opp_amount)) || Number(formData.opp_amount) <= 0)
      newErrors.opp_amount = "The amount must be a positive number";
    if (!formData.probability)
      newErrors.probability = "The probability field is required";
    else if (
      isNaN(Number(formData.probability)) ||
      Number(formData.probability) < 0 ||
      Number(formData.probability) > 100
    )
      newErrors.probability = "Probability must be between 0 and 100";
    if (!formData.stage_id || formData.stage_id === "0")
      newErrors.stage_id = "The stage field is required";
    if (!formData.source_id || formData.source_id === "0")
      newErrors.source_id = "The source field is required";
    if (!formData.status_id || formData.status_id === "0")
      newErrors.status_id = "The status field is required";
    if (!formData.lead_id || formData.lead_id === "0")
      newErrors.lead_id = "The lead field is required";
    if (!formData.expected_close_date)
      newErrors.expected_close_date = "The Expected Close Date field is required";
    return newErrors;
  };

  const prepareFormData = (): FormData => {
    const data = new FormData();
    data.append("opportunitie_name", formData.opportunitie_name);
    data.append("opp_amount", formData.opp_amount);
    data.append("probability", formData.probability);
    data.append("stage_id", formData.stage_id);
    data.append("source_id", formData.source_id);
    // data.append("user_id", formData.user_id); // Will send "0" if not selected
    data.append("status_id", formData.status_id);
    data.append("lead_id", formData.lead_id);
    data.append("expected_close_date", formData.expected_close_date);
    data.append("description", formData.description || "");
    return data;
  };

  const handleDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const formattedDate = date
      ? `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`
      : "";
    setDateOfEvent(formattedDate);
    setFormData((prev) => ({ ...prev, expected_close_date: formattedDate }));
    setErrors((prev) => ({
      ...prev,
      expected_close_date: !formattedDate
        ? "The date field is required"
        : undefined,
    }));
  };

  const isFormValid = () => {
    const isValid =
      formData.opportunitie_name.trim() &&
      formData.opp_amount &&
      !isNaN(Number(formData.opp_amount)) &&
      Number(formData.opp_amount) > 0 &&
      formData.probability &&
      !isNaN(Number(formData.probability)) &&
      Number(formData.probability) >= 0 &&
      Number(formData.probability) <= 100 &&
      formData.stage_id &&
      formData.stage_id !== "0" &&
      formData.source_id &&
      formData.source_id !== "0" &&
      formData.status_id &&
      formData.status_id !== "0" &&
      formData.lead_id &&
      formData.lead_id !== "0" &&
      formData.expected_close_date &&
      !Object.values(errors).some((error) => error);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    // Show alert modal if no leads are available
    if (!leads || leads.length === 0) {
      setErrorMessage("Since there is no lead available here, you cannot add an opportunity.");
      setErrorModalOpen(true);
      setSaving(false);
      return;
    }

    const validationErrors = validateForm();
    if (!isFormValid()) {
      setErrors(validationErrors);
      setSaving(false);
      return;
    }

    if (!token) {
      setErrors({
        opportunitie_name: "Authorization required. Please log in.",
      });
      navigate("/signin");
      setSaving(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/opportunitie/add`,
        prepareFormData(),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          setFormData({
            opportunitie_name: "",
            opp_amount: "",
            probability: "",
            stage_id: "0",
            source_id: "0",
            // user_id: "0",
            status_id: "0",
            lead_id: "0",
            expected_close_date: "",
            description: "",
          });
          setDateOfEvent("");
          navigate("/opportunity-list");
        }, 2000);
      } else if (response.data.status === 422) {
        const apiErrors = response.data.message;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      } else if (response.data.status === 500) {
        setErrors({
          opportunitie_name: response.data.message || "Internal Server Error",
        });
      } else {
        setErrors({
          opportunitie_name: "Unexpected server response. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("API Error:", error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ opportunitie_name: errorMessage });
      if (error.response?.status === 422) {
        const apiErrors = error.response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(
            Object.fromEntries(
              Object.entries(apiErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value[0] : value,
              ])
            )
          );
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Opportunity</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.opportunitieDetails}>
            <ListboxComponent
              value={Array.isArray(leads) ? leads.find((lead) => lead.id === Number(formData.lead_id)) ?? null : null}
              onChange={(lead) => handleInputChange("lead_id", lead ? String(lead.id) : "0")}
              items={Array.isArray(leads) ? leads : []}
              label="Lead Name"
              displayKey="name"
              error={errors.lead_id}
              required
            />

            <div>
              <label className={styles.label}>
                Opportunity Name <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.opportunitie_name}
                onChange={(e) =>
                  handleInputChange("opportunitie_name", e.target.value)
                }
                className={`${styles.inputField} ${errors.opportunitie_name
                  ? styles.inputError
                  : styles.inputDefault
                  }`}
              />
              {errors.opportunitie_name && (
                <span className={styles.errorText}>
                  {errors.opportunitie_name}
                </span>
              )}
            </div>

            <div>
              <label className={styles.label}>
                Amount <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.opp_amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("opp_amount", value);
                }}
                className={`${styles.inputField} ${errors.opp_amount ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.opp_amount && (
                <span className={styles.errorText}>{errors.opp_amount}</span>
              )}
            </div>

            <ListboxComponent
              value={
                stages.find(
                  (stage) => stage.id === Number(formData.stage_id)
                ) ?? null
              }
              onChange={(stage) =>
                handleInputChange("stage_id", stage ? String(stage.id) : "0")
              }
              items={stages}
              error={errors.stage_id}
              label="Stage"
              displayKey="stage_name"
              required
            />

            {/* <ListboxComponent
                value={
                  users.find((user) => user.id === Number(formData.user_id)) ??
                  null
                }
                onChange={(user) =>
                  handleInputChange("user_id", user ? String(user.id) : "0")
                }
                items={users}
                label="Assigned To"
                error={errors.user_id}
                displayKey="name"
                required={false}
              /> */}

            <div>
              <DatePicker
                value={dateOfEvent}
                onChange={handleDateChange}
                label="Expected Close Date"
                error={errors.expected_close_date}
                required
              />
            </div>

            <div>
              <label className={styles.label}>
                Probability <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={formData.probability}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  handleInputChange("probability", value);
                }}
                className={`${styles.inputField} ${errors.probability ? styles.inputError : styles.inputDefault
                  }`}
              />
              {errors.probability && (
                <span className={styles.errorText}>{errors.probability}</span>
              )}
            </div>

            <ListboxComponent
              value={
                sources.find(
                  (source) => source.id === Number(formData.source_id)
                ) ?? null
              }
              onChange={(source) =>
                handleInputChange("source_id", source ? String(source.id) : "0")
              }
              items={sources}
              label="Source"
              error={errors.source_id}
              displayKey="source_name"
              required
            />

            <ListboxComponent
              value={
                statuses.find(
                  (status) => status.id === Number(formData.status_id)
                ) ?? null
              }
              onChange={(status) =>
                handleInputChange("status_id", status ? String(status.id) : "0")
              }
              items={statuses}
              label="Status"
              error={errors.status_id}
              displayKey="status_name"
              required
            />

            <div>
              <label className={styles.label}>Description</label>
              <textarea
                rows={6}
                value={formData.description || ""}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className={`${styles.textareaField}`}
                style={{ textAlign: "justify" }}
              ></textarea>
            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={saving}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => navigate("/opportunity-list")}
            >
              Cancel
            </button>
          </div>

          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className={styles.modal}
          >
            <button
              onClick={() => setSuccessModalOpen(false)}
              className={styles.modalCloseButton}
            ></button>
            <div className="flex justify-center">
              <div className={styles.modalIconContainer}>
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
            <h4 className={styles.modalTitle}>
              Opportunity Added Successfully
            </h4>
            <p className={styles.modalMessage}>
              The opportunity has been added to the system successfully.
            </p>
            <div className={styles.modalButtonContainer}>
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalButton}
              >
                OK
              </button>
            </div>
          </Modal>

          <Modal
            isOpen={isErrorModalOpen}
            onClose={() => setErrorModalOpen(false)}
            className="w-[80%] max-w-[400px] bg-red-100 dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
          >
            <div className="flex justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-yellow-200 text-yellow-600 rounded-full">
                <TriangleAlert className="w-6 h-6" />
              </div>
            </div>
            <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
              Alert!
            </h4>
            <p className="text-gray-600 dark:text-gray-300 mt-2">{errorMessage}</p>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setErrorModalOpen(false)}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
              >
                OK
              </button>
            </div>
          </Modal>

        </form>
      </div>
    </div>
  );
};

export default OpportunityAdd;